// web/src/components/text_effect.ts
//
// Grapheme-correct text-effect renderer (UI-02 / UI-03).
//
// IMPORTANT: letter_indexes are GRAPHEME ORDINALS — the count of segments
// iterated so far, NOT the per-segment "index" field returned by
// Intl.Segmenter (that field is a code-unit offset, NOT a grapheme
// ordinal). Server emits grapheme ordinals per SCHEMA-06 ("zero-based
// Unicode grapheme positions"). Always increment a separate
// `let graphemeOrdinal = 0` counter — do not read the segmenter's
// numeric offset field. (RESEARCH.md Pitfall 1.)
//
// Latvian-specific note: single-codepoint diacritics (š ē ž ļ ķ ū ī č)
// ARE single graphemes. Latvian digraphs (dž, dz) are NOT — UAX #29
// treats them as two graphemes. Empirically verified 2026-05-09:
//   Intl.Segmenter("lv", {granularity:"grapheme"}).segment("džungļi")
//   produces 7 segments at ordinals 0..6 (d, ž, u, n, g, ļ, i).
// Phase 8 seed only highlights words with single-grapheme initials —
// safe for Phase 10. Phase 11 admin authoring guide must include the
// digraph caveat. (RESEARCH.md Pitfall 2.)

import { animate, type DOMKeyframesDefinition } from "motion";
import type { TextEffectType } from "../types/api.js";

/**
 * Render a Latvian word into a row of <span> elements, one per grapheme,
 * applying the effect to graphemes whose ordinal is in letterIndexes.
 *
 *   - "highlighted_letter": colored span (uses --accent-green or --theme-color)
 *   - "missing_letter":     dashed-border span holding the letter as
 *                           transparent text (so layout reserves the
 *                           letter's actual width); reveal flips
 *                           color + border-style.
 *   - "plain":              identity rendering
 */
export function renderTextEffect(
  text: string,
  effectType: TextEffectType,
  letterIndexes: number[] | undefined,
  container: HTMLElement,
): void {
  const idxSet = new Set(letterIndexes ?? []);
  container.replaceChildren();
  const wrap = document.createElement("div");
  wrap.style.cssText =
    "display:flex;flex-wrap:wrap;justify-content:center;gap:0.02em;" +
    "font-family:var(--font-display);font-size:clamp(48px,14vw,180px);" +
    "font-weight:700;letter-spacing:0.04em;line-height:1.0;color:var(--text-primary);";

  // CRITICAL: count graphemes manually. The segmenter's numeric offset field
  // is a code-unit offset, not a grapheme ordinal — do not read it.
  const segmenter = new Intl.Segmenter("lv", { granularity: "grapheme" });
  let graphemeOrdinal = 0;
  for (const { segment } of segmenter.segment(text)) {
    const span = document.createElement("span");
    if (idxSet.has(graphemeOrdinal)) {
      if (effectType === "highlighted_letter") {
        span.textContent = segment;
        span.style.color = "var(--accent-green, var(--theme-color))";
      } else if (effectType === "missing_letter") {
        // Hold the actual letter as transparent text so the box reserves
        // the letter's natural advance width. A fixed `width:1ch` here
        // sized to the display font's "0" glyph, which is narrower than
        // letters like K/M/W — on reveal the letter overflowed the box.
        span.textContent = segment;
        span.dataset.missingLetterChar = segment;
        span.dataset.missingLetterIndex = String(graphemeOrdinal);
        span.dataset.revealed = "false";
        span.style.cssText =
          "display:inline-block;color:transparent;" +
          "border:3px dashed var(--theme-color);border-radius:6px;" +
          "padding:0 0.05em;box-sizing:border-box;";
        span.setAttribute("aria-label", "trūkstošs burts");
      } else {
        span.textContent = segment;
      }
    } else {
      span.textContent = segment;
    }
    wrap.appendChild(span);
    graphemeOrdinal++;
  }
  container.appendChild(wrap);

  // 200ms easeOut fade-in to match Phase 4 v1 contract.
  animate(
    wrap as Element,
    { opacity: [0, 1] } as DOMKeyframesDefinition,
    { duration: 0.2, ease: "easeOut" },
  );
}
